import { Injectable, signal, effect } from '@angular/core'

export type Theme = 'light' | 'dark'

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly THEME_KEY = 'media-hub-theme'

  private currentTheme = signal<Theme>(this.getInitialTheme())

  theme = this.currentTheme.asReadonly()

  constructor() {
    effect(() => {
      this.applyTheme(this.currentTheme())
    })
  }

  toggleTheme(): void {
    const newTheme = this.currentTheme() === 'dark' ? 'light' : 'dark'
    this.setTheme(newTheme)
  }

  setTheme(theme: Theme): void {
    this.currentTheme.set(theme)
    localStorage.setItem(this.THEME_KEY, theme)
  }

  isDark(): boolean {
    return this.currentTheme() === 'dark'
  }

  isLight(): boolean {
    return this.currentTheme() === 'light'
  }

  private getInitialTheme(): Theme {
    const savedTheme = localStorage.getItem(this.THEME_KEY) as Theme | null
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      return savedTheme
    }

    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }

    return 'dark'
  }

  private applyTheme(theme: Theme): void {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', theme)

      document.documentElement.classList.remove('light-theme', 'dark-theme')
      document.documentElement.classList.add(`${theme}-theme`)
    }
  }
}
